import * as turf from '@turf/turf';
import { GeoJSONSource, LngLat, Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl';
import { v4 } from 'uuid';
import assert from '../../../utils/assert';
import { DrawingObjectCenterUnits, DrawingObjectOffsetUnits, DrawingObjectRectangleGeometry } from '../types';
import { FeDrawMode } from './draw-mode';

export class FeRectangleEditMode extends FeDrawMode {
    constructor(
        private readonly map: MaplibreMap,
        private readonly source: GeoJSONSource
    ) {
        super();
    }

    private resizeVertex: number | null = null;
    private lastDragPoint: LngLat | null = null;
    private originalUnits: DrawingObjectOffsetUnits = DrawingObjectOffsetUnits.degrees;
    private dragging = false;
    private center: number[] = [0, 0];
    private width: number = 0;
    private height: number = 0;
    private feature: GeoJSON.Feature<GeoJSON.Polygon> | null = null;
    private points: GeoJSON.Feature<GeoJSON.Point, { id: number; type: 'vertex'; active: boolean }>[] = [];

    override cancel(): void {
        this.source.setData(turf.featureCollection([]));
        this.dragging = false;
    }

    override onMouseMove(e: MapMouseEvent): void {
        if (this.dragging) {
            assert(this.lastDragPoint);
            const deltaLng = e.lngLat.lng - this.lastDragPoint.lng;
            const deltaLat = e.lngLat.lat - this.lastDragPoint.lat;
            this.lastDragPoint = e.lngLat;

            if (this.resizeVertex != null) {
                this.center[0] += deltaLng / 2;
                this.center[1] += deltaLat / 2;
                switch (this.resizeVertex) {
                    case 0:
                        this.width -= deltaLng;
                        this.height += deltaLat;
                        break;
                    case 1:
                        this.width += deltaLng;
                        this.height += deltaLat;
                        break;
                    case 2:
                        this.width += deltaLng;
                        this.height -= deltaLat;
                        break;
                    case 3:
                        this.width -= deltaLng;
                        this.height -= deltaLat;
                        break;
                }
            } else {
                if (Math.abs(this.center[1] + (Math.sign(deltaLat) * this.height) / 2 + deltaLat) > 85) {
                    return;
                }
                this.center[0] += deltaLng;
                this.center[1] += deltaLat;
            }

            this.updateGeometry();
        }
    }

    override onMouseDown(e: MapMouseEvent): void {
        if (this.resizeVertex != null) {
            this.setActive(this.resizeVertex, false);
        }

        const features = this.map.queryRenderedFeatures(e.point, { layers: ['fe-drawing-points-outline', 'fe-drawing-fill'] });

        if (features.length === 0) {
            return;
        }

        e.preventDefault();
        this.lastDragPoint = e.lngLat;
        this.dragging = true;

        // We clicked on a vertex
        this.resizeVertex = features.find((x) => x.layer.id === 'fe-drawing-points-outline')?.properties.id ?? null;

        if (this.resizeVertex != null) {
            this.setActive(this.resizeVertex, true);
        }
    }

    override onMouseUp(e: MapMouseEvent): void {
        this.dragging = false;
    }

    override edit(geometry: DrawingObjectRectangleGeometry): void {
        this.dragging = false;
        this.resizeVertex = null;
        this.center = [geometry.centerX, geometry.centerY];
        this.originalUnits = geometry.sizeUnits;

        this.feature = {
            id: v4(),
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [[]]
            },
            properties: {}
        };

        if (geometry.sizeUnits === DrawingObjectOffsetUnits.miles) {
            const halfWidth = geometry.width / 2;
            const halfHeight = geometry.height / 2;
            const e = turf.rhumbDestination(this.center, halfWidth, 90, { units: 'nauticalmiles' }).geometry.coordinates;
            const w = turf.rhumbDestination(this.center, halfWidth, -90, { units: 'nauticalmiles' }).geometry.coordinates;
            const n = turf.rhumbDestination(this.center, halfHeight, 0, { units: 'nauticalmiles' }).geometry.coordinates;
            const s = turf.rhumbDestination(this.center, halfHeight, 180, { units: 'nauticalmiles' }).geometry.coordinates;
            this.width = turf.rhumbDistance([w[0], this.center[1]], [e[0], this.center[1]], { units: 'degrees' });
            this.height = turf.rhumbDistance([this.center[0], n[1]], [this.center[0], s[1]], { units: 'degrees' });
        } else {
            this.width = geometry.width;
            this.height = geometry.height;
        }

        const halfWidth = this.width / 2;
        const halfHeight = this.height / 2;
        this.points = [
            turf.point([this.center[0] + halfWidth, this.center[1] + halfHeight], { type: 'vertex', id: 0, active: false }, { id: v4() }),
            turf.point([this.center[0] - halfWidth, this.center[1] + halfHeight], { type: 'vertex', id: 1, active: false }, { id: v4() }),
            turf.point([this.center[0] - halfWidth, this.center[1] - halfHeight], { type: 'vertex', id: 2, active: false }, { id: v4() }),
            turf.point([this.center[0] - halfWidth, this.center[1] - halfHeight], { type: 'vertex', id: 3, active: false }, { id: v4() })
        ];

        this.source.setData({
            type: 'FeatureCollection',
            features: [this.feature, ...this.points]
        });
        this.updateGeometry();
    }

    override getEdited(): DrawingObjectRectangleGeometry {
        assert(this.feature);
        const center = turf.center(this.feature).geometry.coordinates;
        const bbox = turf.bbox(this.feature);
        let width;
        let height;
        if (this.originalUnits === DrawingObjectOffsetUnits.degrees) {
            width = Math.abs(bbox[2] - bbox[0]);
            height = Math.abs(bbox[1] - bbox[3]);
        } else {
            width = turf.rhumbDistance([bbox[0], center[1]], [bbox[2], center[1]], { units: 'nauticalmiles' });
            height = turf.rhumbDistance([center[0], bbox[1]], [center[0], bbox[3]], { units: 'nauticalmiles' });
        }

        return {
            centerX: center[0],
            centerY: center[1],
            centerUnits: DrawingObjectCenterUnits.latLng,
            width: width,
            height: height,
            sizeUnits: this.originalUnits
        };
    }

    private setActive(vertexId: number, value: boolean) {
        this.source.updateData({
            update: [
                {
                    id: this.points[vertexId].id as string,
                    addOrUpdateProperties: [
                        {
                            key: 'active',
                            value
                        }
                    ]
                }
            ]
        });
    }

    private updateGeometry() {
        assert(this.feature);

        const halfWidth = this.width / 2;
        const halfHeight = this.height / 2;
        const coords = this.feature.geometry.coordinates[0];

        coords[0] = [this.center[0] - halfWidth, this.center[1] + halfHeight];
        coords[1] = [this.center[0] + halfWidth, this.center[1] + halfHeight];
        coords[2] = [this.center[0] + halfWidth, this.center[1] - halfHeight];
        coords[3] = [this.center[0] - halfWidth, this.center[1] - halfHeight];
        coords[4] = [...coords[0]];

        for (let i = 0; i < 4; i++) {
            this.points[i].geometry.coordinates = coords[i];
        }

        this.source.updateData({
            update: [
                {
                    id: this.feature.id as string,
                    newGeometry: this.feature.geometry
                },
                ...this.points.map((x) => ({
                    id: x.id as string,
                    newGeometry: x.geometry
                }))
            ]
        });
    }
}
